import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthLayout, {
  AppleIcon,
  AuthDivider,
  GoogleIcon,
  PasswordToggle,
} from '../../components/AuthLayout';

export default function RegisterPage() {
  const { isAuthenticated, register, loginWithGoogle, loginWithApple, isFirebaseConfigured } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [providerLoading, setProviderLoading] = useState('');
  const navigate = useNavigate();

  if (isAuthenticated) return <Navigate to="/" replace />;

  const isBusy = loading || Boolean(providerLoading);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const result = await register({ name: name.trim(), email: email.trim(), password });
    setLoading(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    navigate('/', { replace: true });
  };

  const handleProviderLogin = async (provider) => {
    setError('');
    setProviderLoading(provider);
    const result = provider === 'google' ? await loginWithGoogle() : await loginWithApple();
    setProviderLoading('');
    if (!result.ok) {
      setError(result.message);
      return;
    }
    navigate('/', { replace: true });
  };

  return (
    <AuthLayout
      eyebrow="Create your space"
      title="Start with KinetoCut"
      subtitle="Set up your account in a moment. Your original media remains on your device."
      footer={(
        <p>Already have an account? <Link to="/login">Sign in</Link></p>
      )}
    >
      <form className="auth-form" onSubmit={handleSubmit} aria-busy={isBusy}>
        <div className="form-row">
          <label htmlFor="name">Full name</label>
          <div className="input-wrap">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="input"
              autoComplete="name"
              placeholder="Your name"
              disabled={isBusy}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <label htmlFor="email">Email address</label>
          <div className="input-wrap">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 6h16v12H4V6Zm0 1 8 6 8-6" /></svg>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="input"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              disabled={isBusy}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="label-row">
            <label htmlFor="password">Password</label>
            <span>At least 6 characters</span>
          </div>
          <div className="input-wrap has-action">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="input"
              autoComplete="new-password"
              placeholder="Create a password"
              minLength={6}
              disabled={isBusy}
              required
            />
            <PasswordToggle visible={showPassword} onClick={() => setShowPassword((value) => !value)} />
          </div>
        </div>

        {error && <div className="status-box status-error" role="alert"><span>!</span><p>{error}</p></div>}

        <button type="submit" className="button button-primary button-full button-large" disabled={isBusy}>
          {loading && <span className="spinner spinner-dark" />}
          {loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <AuthDivider />

      <div className="social-stack">
        <button type="button" className="social-button" onClick={() => handleProviderLogin('google')} disabled={!isFirebaseConfigured || isBusy}>
          {providerLoading === 'google' ? <span className="spinner" /> : <GoogleIcon />}
          Continue with Google
        </button>
        <button type="button" className="social-button" onClick={() => handleProviderLogin('apple')} disabled={!isFirebaseConfigured || isBusy}>
          {providerLoading === 'apple' ? <span className="spinner" /> : <AppleIcon />}
          Continue with Apple
        </button>
        {!isFirebaseConfigured && (
          <p className="form-note">Social sign-in becomes available when Firebase is configured. Email accounts still work locally.</p>
        )}
      </div>
    </AuthLayout>
  );
}
